import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPartnerLeads,
  acceptLead,
  rejectLead,
  toggleAvailability,
  fetchPartnerStats,
} from '../store/partnerSlice';
import { logout } from '../store/userSlice';
import {
  DollarSign,
  Calendar,
  Star,
  TrendingUp,
  LogOut,
  MapPin,
  Clock,
  Phone,
  Check,
  X,
  Power,
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'react-toastify';

const DashboardPartner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { leads, stats, isAvailable, loading } = useSelector((state) => state.partner);

  useEffect(() => {
    if (!user || user.role !== 'partner') {
      navigate('/login');
      return;
    }
    dispatch(fetchPartnerLeads());
    dispatch(fetchPartnerStats());
  }, [dispatch, user, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleToggleAvailability = async () => {
    try {
      await dispatch(toggleAvailability()).unwrap();
      toast.success(isAvailable ? 'You are now unavailable' : 'You are now available');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const handleAcceptLead = async (leadId) => {
    try {
      await dispatch(acceptLead(leadId)).unwrap();
      toast.success('Lead accepted successfully!');
    } catch (error) {
      toast.error('Failed to accept lead');
    }
  };

  const handleRejectLead = async (leadId) => {
    try {
      await dispatch(rejectLead(leadId)).unwrap();
      toast.info('Lead rejected');
    } catch (error) {
      toast.error('Failed to reject lead');
    }
  };

  const getTimeSlot = (slot) => {
    const slots = {
      morning: '8 AM - 12 PM',
      afternoon: '12 PM - 4 PM',
      evening: '4 PM - 8 PM',
    };
    return slots[slot] || slot;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-accent to-purple-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar || 'https://randomuser.me/api/portraits/women/44.jpg'}
              alt={user?.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h2 className="text-xl font-bold text-gray-800">{user?.name}</h2>
              <p className="text-sm text-gray-600">Partner</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Availability Toggle */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleToggleAvailability}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                isAvailable
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              <Power className="w-4 h-4" />
              <span className="hidden md:inline">
                {isAvailable ? 'Available' : 'Unavailable'}
              </span>
            </motion.button>
            <NotificationBell />
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg">
            <DollarSign className="w-8 h-8 mb-2" />
            <h3 className="text-2xl font-bold mb-1">₹{stats.totalEarnings?.toLocaleString()}</h3>
            <p className="text-sm opacity-90">Total Earnings</p>
            <p className="text-xs mt-1 opacity-75">+₹{stats.thisMonthEarnings?.toLocaleString()} this month</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <Calendar className="w-8 h-8 text-primary mb-2" />
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{stats.completedSessions}</h3>
            <p className="text-sm text-gray-600">Completed Sessions</p>
            <p className="text-xs text-gray-500 mt-1">+{stats.thisMonthSessions} this month</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <Star className="w-8 h-8 text-yellow-400 mb-2" />
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{stats.rating}</h3>
            <p className="text-sm text-gray-600">Rating</p>
            <p className="text-xs text-gray-500 mt-1">{stats.reviewCount} reviews</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <TrendingUp className="w-8 h-8 text-primary mb-2" />
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{stats.responseRate}%</h3>
            <p className="text-sm text-gray-600">Response Rate</p>
            <p className="text-xs text-gray-500 mt-1">{stats.pendingBookings} pending</p>
          </div>
        </div>

        {/* New Leads */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              New Booking Requests ({leads.length})
            </h2>
            {!isAvailable && (
              <span className="text-sm text-orange-600 font-medium">
                ⚠️ You're currently unavailable
              </span>
            )}
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-200 h-40 rounded-xl" />
              ))}
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No new booking requests</p>
              <p className="text-sm text-gray-500 mt-2">
                Make sure you're available to receive leads
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="border-2 border-primary/20 rounded-xl p-6 hover:shadow-md transition-all bg-purple-50/50"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={lead.user.avatar}
                        alt={lead.user.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {lead.user.name}
                        </h3>
                        <p className="text-gray-600">{lead.service}</p>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <MapPin className="w-4 h-4" />
                          <span>{lead.distance} km away</span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(lead.createdAt).toLocaleTimeString()}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{lead.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{getTimeSlot(lead.preferredTime)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{lead.user.phone}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAcceptLead(lead.id)}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                    >
                      <Check className="w-5 h-5" />
                      Accept
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleRejectLead(lead.id)}
                      className="flex-1 flex items-center justify-center gap-2 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                    >
                      <X className="w-5 h-5" />
                      Reject
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPartner;
